import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-hot-toast';
import {
  PublishData,
  UpdateOneData,
  capitalize,
  getDataWithFilterUrl,
  sendSubmissionUpdationAlert,
  separateDasFromUrl,
  updateFormSchemaWithRealtion,
} from "../../controllers/APIController";
// import { logMetricCreated } from "../../utils/auditLogger";
import { Button, Chip, Spinner, Card, CardBody, CardHeader, Input, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Textarea, Select, SelectItem } from "@nextui-org/react";
import {
  getFieldMoreInfoContent,
  getFormulaData,
  getSchemaForOrg,
  hasCommonElement,
  increasedMetricCurrentCount,
} from "../../utils/helper";
import {
  BooleanInput,
  DateInput,
  DropDownInput,
  IntegerInput,
  MetricSelectorInput,
  PasswordInput,
  RelationDropDownInput,
  SliderInput,
  TextAreaInput,
  TextInput,
} from "../FormBuilder/InputField/InputField";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { checkValidation } from "../../utils/checkValidation";
import FileEvidenceInput from "../FormBuilder/InputField/FileEvidenceInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faMapMarkerAlt, faDollarSign, faChartBar, faCog, faFilter, faSave, faTimes, faCalculator } from "@fortawesome/free-solid-svg-icons";
import { MdOutlineContentCopy } from "react-icons/md";
import InfoToolTip from "../../components/utils/InfoToolTip";

import useGetOrgFramework from "../../hooks/useGetOrgFramework";
import { metricColor } from "../../utils/constant";
import LoadingPage from "../../components/Loader/LoadingPage";
import MetricFrameworkInfo from "./MetricFrameworkInfo";
import ConditionalField from "../../pages/FormBuilder/InputField/ConditionalField";
import SubheadingField from "../../pages/FormBuilder/InputField/SubheadingField";
import { groupFormDataBySubheadings, flattenGroupedFormData, getSubheadingStructure } from "../../utils/formGrouping.utils";

/**
 * Initialize form state based on schema configuration
 * Creates default values for each form field based on its type
 * @param {Array} arr - Array of [fieldName, fieldConfig] pairs
 * @returns {Object} Object with field names as keys and default values
 */
const getInitialState = (arr) => {
  const data = {};

  arr?.forEach((item) => {
    // Skip subheading fields as they don't need form data
    if (item[1].type === "subheading") {
      return;
    }
    
    // Set default values based on field type
    if (item[1].type === "boolean") {
      data[item[0]] = "false";
    } else if (item[1].type === "slider") {
      data[item[0]] = item[1].value || 0;
    } else if (item[1].type === "file") {
      data[item[0]] = [];
    } else if (item[1].type === "metricSelector") {
      data[item[0]] = "";
    } else {
      data[item[0]] = "";
    }
  });
  return data;
};

/**
 * Helper function to get appropriate icons for different sections
 * Maps section titles to FontAwesome icons for visual representation
 * @param {string} sectionTitle - The title of the section
 * @returns {Icon} FontAwesome icon component
 */
const getSectionIcon = (sectionTitle) => {
  const title = sectionTitle.toLowerCase();
  
  // Map section titles to appropriate icons
  if (title.includes('basic') || title.includes('general') || title.includes('information')) {
    return faCog;
  } else if (title.includes('organization') || title.includes('company')) {
    return faBuilding;
  } else if (title.includes('address') || title.includes('location')) {
    return faMapMarkerAlt;
  } else if (title.includes('financial') || title.includes('cost') || title.includes('price')) {
    return faDollarSign;
  } else if (title.includes('data') || title.includes('metric') || title.includes('certificate') || title.includes('renewable')) {
    return faChartBar;
  } else {
    return faCog; // Default icon for other sections
  }
};
/**
 * Update evidence references to link them to the current metric response
 * Connects uploaded evidence files to the metric response for tracking
 * @param {Object} formdata - Form data containing evidence IDs
 * @param {Array} formschemaArray - Array of [fieldName, fieldConfig] pairs
 * @param {number} referenceId - ID of the metric response to link evidence to
 */
const updateEvidenceReferences = async (
  formdata,
  formschemaArray,
  referenceId
) => {
  const promisesResponses = formschemaArray.map(async (row) => {
    if (row[1]?.type === "file") {
      const evidenceIdList = formdata[row[0]];

      const result = evidenceIdList?.map(async (id) => {
        const uploadData = {
          ReferenceList: {
            connect: [+referenceId],
          },
        };

        return await UpdateOneData("evidences", +id, uploadData);
      });
      return result;
    }
    return null;
  });

  await Promise.all(promisesResponses);
};

/**
 * CreateMetricForm Component
 * Form component for creating new metric entries
 * Handles form validation, data submission, and evidence file uploads
 * Supports dynamic form generation based on metric schema
 */
const CreateMetricForm = () => {
  // User and organization information from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const userId = userLogged && userLogged.user.id;

  // Loading and submission states
  const [loader, setLoader] = useState(true);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResults, setCalculationResults] = useState({});
  
  // Form data and schema states
  const [collectionInfo, setCollectionInfo] = useState();
  const [formdata, setFormData] = useState({});
  const [formSchema, setFormSchema] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Category management states
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  // Framework and navigation hooks
  const { orgFrameworkDetails } = useGetOrgFramework(orgInfo);
  const navigate = useNavigate();

  // URL parameters for routing
  const { _orgId: currOrg, _categoryId, _metricId } = useParams();

  // Check if formula fields are visible based on schema
  const isFormulaVisible =
    collectionInfo?.attributes?.JsonSchema?.formula?.length > 0 ? true : false;

  // Organization filter URL for API calls
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  // Metric title and description from collection info
  let headingTitle = capitalize(separateDasFromUrl(_metricId));
  let description = collectionInfo?.attributes?.Description;


  const filteredUrl =
    orgFilterUrl +
    "&filters[Category][$eqi]=" +
    _categoryId.replaceAll("-", " ") +
    "&filters[Name][$eqi]=" +
    decodeURIComponent(_metricId).replaceAll("-", " ").replaceAll("&amp;", "&");

  /**
   * Fetch categories from metrics API
   * Retrieves all available metric categories for the current organization
   * Updates the dynamic categories state for filtering
   */
  const fetchCategories = async () => {
    if (!orgInfo) return;
    
    setIsLoadingCategories(true);
    try {
      const data = await getDataWithFilterUrl("metrics", orgFilterUrl);
      const metricsList = data?.data || [];
      
      // Extract unique categories from metrics data
      const uniqueCategories = [...new Set(
        metricsList.map(item => item?.attributes?.Category).filter(Boolean)
      )].sort();
      
      setDynamicCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      // Fallback to empty array if API fails
      setDynamicCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  /**
   * Handle category selection from dropdown
   * Processes different key formats and updates selected category state
   * @param {string} key - The selected category key
   */
  const handleCategorySelection = (key) => {
    // If key is empty string, it means "Clear Filter"
    if (key === "") {
      setSelectedCategory("");
    } else if (key.startsWith("filter-")) {
      // Extract category name from the key (remove "filter-" prefix)
      const categoryName = key.replace("filter-", "");
      setSelectedCategory(categoryName);
    } else {
      // Fallback for any other keys
      setSelectedCategory(key);
    }
  };

  /**
   * Handle form field value changes
   * Updates form data state when user inputs values
   * Handles special cases for boolean fields
   * @param {string} title - Field name
   * @param {any} value - New field value
   * @param {string} type - Field type (boolean, text, etc.)
   */
  const changeHandler = async (title, value, type) => {
    console.log("🔄 Form field changed:", { title, value, type });
    console.log("📊 Previous form data:", formdata);
    
    // Handle boolean fields specially
    if (type === "boolean") {
      if (value === "") {
        setFormData({ ...formdata, [title]: "false" });
        console.log("✅ Boolean field updated:", { title, value: "false" });
      } else {
        setFormData({ ...formdata, [title]: value });
        console.log("✅ Boolean field updated:", { title, value });
      }
    } else {
      setFormData({ ...formdata, [title]: value });
      console.log("✅ Field updated:", { title, value });
    }
    
    const updatedFormData = { ...formdata, [title]: value };
    console.log("📋 Updated form data:", updatedFormData);
    
    // Special logging for fugitive emissions fields
    if (title === "Quantity" || title === "Greenhouse gas") {
      console.log("🌿 Fugitive emissions field updated:", {
        field: title,
        value: value,
        type: typeof value
      });
      
      // Log current state of both required fields
      const currentQuantity = title === "Quantity" ? value : formdata["Quantity"];
      const currentGas = title === "Greenhouse gas" ? value : formdata["Greenhouse gas"];
      
      console.log("🔍 Current fugitive emissions state:", {
        Quantity: currentQuantity,
        "Greenhouse gas": currentGas
      });
    }
  };

  /**
   * Helper function to validate and log form data for calculations
   * Provides detailed debugging information about field values and structure
   * @param {Object} formData - The form data to validate
   * @param {Array} schema - The form schema for reference
   * @returns {Object} Validation result with status and details
   */
  const validateFormDataForCalculation = (formData, schema) => {
    console.log("🔍 VALIDATING FORM DATA FOR CALCULATION");
    console.log("📋 Form data to validate:", formData);
    console.log("📋 Schema reference:", schema);
    
    const validation = {
      isValid: true,
      missingFields: [],
      invalidFields: [],
      fieldDetails: {},
      recommendations: []
    };
    
    // Check for fugitive emissions specific fields
    if (collectionInfo?.attributes?.Name?.toLowerCase().includes("fugitive")) {
      console.log("🌿 FUGITIVE EMISSIONS VALIDATION");
      
      const requiredFields = ["Quantity", "Greenhouse gas"];
      
      requiredFields.forEach(field => {
        const value = formData[field];
        const fieldInfo = {
          name: field,
          value: value,
          type: typeof value,
          isEmpty: !value || value === "" || value === "null",
          isNumeric: !isNaN(parseFloat(value)) && parseFloat(value) > 0
        };
        
        validation.fieldDetails[field] = fieldInfo;
        
        if (fieldInfo.isEmpty) {
          validation.missingFields.push(field);
          validation.isValid = false;
          validation.recommendations.push(`Fill in the "${field}" field`);
        } else if (field === "Quantity" && !fieldInfo.isNumeric) {
          validation.invalidFields.push(field);
          validation.isValid = false;
          validation.recommendations.push(`"${field}" must be a positive number`);
        }
        
        console.log(`  ${field}:`, fieldInfo);
      });
      
      // Log overall validation status
      console.log("📊 Validation Summary:", {
        isValid: validation.isValid,
        missingFields: validation.missingFields,
        invalidFields: validation.invalidFields,
        recommendations: validation.recommendations
      });
    }
    
    // Log all available fields for debugging
    console.log("🔍 ALL AVAILABLE FIELDS:");
    Object.keys(formData).forEach(field => {
      const value = formData[field];
      console.log(`  ${field}: "${value}" (${typeof value})`);
    });
    
    return validation;
  };

  /**
   * Handle copying text to clipboard
   * Copies the provided text to user's clipboard
   * @param {string} text - Text to copy to clipboard
   */
  const handleCopyClick = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here if needed
      console.log("Copied to clipboard");
    });
  };

  // eslint-disable-next-line no-unused-vars
  const curlMessage = `curl --location '${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/send' \n
  --header 'Content-Type: application/json' \n
  --header 'apikey: <Enter Your API Key>' \n
  --data '{
      "metric_id": "${collectionInfo?.id}",
      "response": <Enter Your JSON Response>
      
  }'`;

  /**
   * Handle form submission
   * Validates form data, processes formulas, and submits to backend
   * Handles evidence file references and metric counter updates
   */
  const submitHandler = async () => {
    console.log("🚀 Submit handler called");
    console.log("👤 User logged in:", !!userLogged);
    console.log("🆔 User ID:", userId);
    console.log("🏢 Organization ID:", orgInfo);
    console.log("🏢 Organization ID type:", typeof orgInfo);
    console.log("👤 User logged details:", userLogged?.user);
    
    // Validate form data
    const isValid = checkValidation(formSchema, formdata, setValidationErrors);
    if (!isValid) {
      console.log("❌ Form validation failed");
      return;
    }

    console.log("✅ Form validation passed");
    console.log("📋 Form data before processing:", formdata);

    // Create a clean copy of form data for processing
    const submissionFormData = { ...formdata };
    
    // Convert empty strings to "null" for backend compatibility
    Object.keys(submissionFormData).forEach((key) => {
      if (submissionFormData[key] === "") {
        submissionFormData[key] = "null";
      }
    });
    
    console.log("🧹 Cleaned form data for submission:", submissionFormData);
    
    // Validate that required fields for fugitive emissions calculation have values
    if (collectionInfo?.attributes?.Name?.toLowerCase().includes("fugitive")) {
      const requiredFields = ["Quantity", "Greenhouse gas"];
      const missingFields = requiredFields.filter(field => 
        !submissionFormData[field] || submissionFormData[field] === "null"
      );
      
      if (missingFields.length > 0) {
        console.error("❌ Missing required fields for submission:", missingFields);
        alert(`Cannot submit: Missing values for required fields: ${missingFields.join(", ")}. Please fill in all required fields before submitting.`);
        return;
      }
      
      // Additional validation for fugitive emissions
      const quantity = parseFloat(submissionFormData["Quantity"]);
      if (isNaN(quantity) || quantity <= 0) {
        console.error("❌ Invalid quantity value:", submissionFormData["Quantity"]);
        alert(`Invalid quantity value: "${submissionFormData["Quantity"]}". Quantity must be a positive number.`);
        return;
      }
      
      if (!submissionFormData["Greenhouse gas"] || submissionFormData["Greenhouse gas"] === "null") {
        console.error("❌ Missing greenhouse gas selection");
        alert("Please select a greenhouse gas type before submitting.");
        return;
      }
      
      console.log("🌿 Fugitive emissions validation passed");
      console.log("📊 Quantity:", submissionFormData["Quantity"]);
      console.log("🌡️ Greenhouse gas:", submissionFormData["Greenhouse gas"]);
    }

    // Calculate formula results
    console.log("🧮 Calculating formula results...");
    const formulaData = await getFormulaData(
      collectionInfo?.attributes?.JsonSchema?.formula,
      submissionFormData
    );
    
    console.log("📥 Formula calculation results:", formulaData);

    // Group form data by subheadings for better organization
    // Use original schema with proper field order to preserve all field metadata including assignedGroup
    const jsonSchema = collectionInfo?.attributes?.JsonSchema || {};
    const properties = jsonSchema.properties || {};
    const fieldOrder = jsonSchema.fieldOrder || [];
    
    let originalResponseArr = [];
    
    // Use field order if available to ensure subheadings are processed first
    if (fieldOrder.length > 0) {
      fieldOrder.forEach(key => {
        if (properties[key]) {
          originalResponseArr.push([key, properties[key]]);
        }
      });
      
      // Add any missing fields (backwards compatibility)
      Object.keys(properties).forEach(key => {
        if (!fieldOrder.includes(key)) {
          originalResponseArr.push([key, properties[key]]);
        }
      });
    } else {
      // Fallback: process all fields
      originalResponseArr = Object.keys(properties).map(key => [key, properties[key]]);
    }
    const groupedFormData = groupFormDataBySubheadings(submissionFormData, originalResponseArr);
    console.log("📊 Form grouping debug:", {
      originalFormData: submissionFormData,
      originalResponseArr,
      groupedFormData
    });
    
    const logsInfo = {
      by: userId,
      operation: "created",
      timestamp: Date.now(),
    };

    console.log("📋 CollectionInfo:", collectionInfo);
    console.log("📋 CollectionInfo.attributes:", collectionInfo?.attributes);
    
    const publishData = {
      Metric_Id: collectionInfo.attributes.Name,
      Status:
        collectionInfo?.attributes?.WorkFlow === "Default" ? "Done" : "Created",
      JsonSchema: { 
        groupedData: groupedFormData,
        flatData: { ...submissionFormData, ...formulaData },
        ...formulaData 
      },
      Organization: orgInfo,
      users_permissions_user: {
        disconnect: [],
        connect: [
          {
            id: userId,
            position: {
              end: true,
            },
          },
        ],
      },
      Logs: [logsInfo],
    };
    
    console.log("📤 Publish data prepared:", publishData);

    setIsSubmittingForm(true);
    
    try {
      console.log("📤 Submitting form data:", publishData);
      let result = await PublishData({ data: publishData }, "responses");
      
      if (!result || !result.data) {
        throw new Error("Failed to create entry. No response received from server.");
      }
      
      console.log("✅ Entry created successfully:", result);

      // Log the metric creation for audit purposes
      // try {
      //   await logMetricCreated(
      //     result?.data?.id || "unknown",
      //     collectionInfo?.attributes?.Name || "Unknown Metric",
      //     {
      //       category: _categoryId,
      //       metricId: _metricId,
      //       organizationId: orgInfo,
      //       status: publishData.Status,
      //       workflow: collectionInfo?.attributes?.WorkFlow
      //     }
      //   );
      // } catch (error) {
      //   console.error("Error logging metric creation:", error);
      // }

      const submissionalertData = {
        orgId: orgInfo,
        UserId: userLogged && userLogged.user.email,
        operation: "created",
        entryId: collectionInfo?.attributes?.Name,
        linkurl: `${process.env.REACT_APP_IP_ADDRESS}/${currOrg}/metrics/${_categoryId}/${_metricId}/${result?.data?.id}`,
      };
      
      try {
        await sendSubmissionUpdationAlert(submissionalertData);
      } catch (alertError) {
        console.warn("Failed to send submission alert:", alertError);
      }
      
      try {
        await increasedMetricCurrentCount(
          collectionInfo?.id,
          result?.data?.attributes?.createdAt
        );
      } catch (countError) {
        console.warn("Failed to increase metric count:", countError);
      }

              try {
          await updateEvidenceReferences(submissionFormData, formSchema, result?.data?.id);
        } catch (evidenceError) {
          console.warn("Failed to update evidence references:", evidenceError);
        }

      if (collectionInfo?.attributes?.FrequencyValue === 0) {
        try {
          await UpdateOneData("metrics", collectionInfo?.id, {
            lastResponseId: result?.data?.id,
          });
        } catch (updateError) {
          console.warn("Failed to update metric:", updateError);
        }
      }

      // TODO: Workflow integration - to be implemented when new workflow system is ready
      // try {
      //   // await syncWorkflow(
      //   //   result.data.id,
      //   //   collectionInfo.id,
      //   //   publishData.Status, // "Created"
      //   //   userId,
      //   //   orgInfo
      //   // );
      //   console.log('✅ Workflow synchronized successfully');
      // } catch (workflowError) {
      //   console.warn('⚠️ Workflow sync failed:', workflowError);
      //   // Don't break the main submission - just log the warning
      // }

      setIsSubmittingForm(false);
      navigate(`/${currOrg}/metrics/${_categoryId}/${_metricId}`);
      
    } catch (error) {
      console.error("Error creating entry:", error);
      setIsSubmittingForm(false);
      
      // Show error to user
      alert(`Failed to create entry: ${error.message || 'Unknown error occurred'}`);
    }
  };

  const testCalculationHandler = async () => {
    console.log("🧮 CALCULATION STARTED - Debugging form data...");
    console.log("📋 Current form data:", formdata);
    console.log("🔍 Form schema:", formSchema);
    
    const isValid = checkValidation(formSchema, formdata, setValidationErrors);
    if (!isValid) {
      console.log("❌ Form validation failed, cannot proceed with calculation");
      toast.error("Please fix validation errors before calculating");
      return;
    }
    
    console.log("✅ Form validation passed, proceeding with calculation");
    
    setIsCalculating(true);
    
    // Create a clean copy of form data for calculation
    const calculationFormData = { ...formdata };
    
    // Convert empty strings to "null" for backend compatibility
    Object.keys(calculationFormData).forEach((key) => {
      if (calculationFormData[key] === "") {
        calculationFormData[key] = "null";
      }
    });
    
    console.log("🧹 Cleaned form data for calculation:", calculationFormData);
    
    // Use the enhanced validation function
    const validation = validateFormDataForCalculation(calculationFormData, formSchema);
    
    if (!validation.isValid) {
      console.error("❌ Form validation failed for calculation");
      console.error("📊 Validation details:", validation);
      
      const errorMessage = validation.recommendations.length > 0 
        ? validation.recommendations.join(". ") + "."
        : "Please check all required fields before calculating.";
      
      setCalculationResults({
        error: {
          result: "Validation Failed",
          reasoning: errorMessage
        }
      });
      setIsCalculating(false);
      toast.error("Validation failed. Please check all required fields.");
      return;
    }
    
    console.log("✅ Form validation passed for calculation");
    
    try {
      console.log("🚀 Sending data to formula calculation...");
      console.log("📝 Formula configuration:", collectionInfo?.attributes?.JsonSchema?.formula);
      console.log("📊 Data being sent:", calculationFormData);
      
      // Check if formulas exist
      const formulas = collectionInfo?.attributes?.JsonSchema?.formula;
      if (!formulas || !Array.isArray(formulas) || formulas.length === 0) {
        throw new Error("No calculation formulas found for this metric.");
      }
      
      const formulaData = await getFormulaData(formulas, calculationFormData);
      
      console.log("📥 Raw formula response:", formulaData);

      // Check if the response contains an error
      if (formulaData.error) {
        console.error("❌ Formula calculation returned error:", formulaData.error);
        
        // Try fallback calculation for simple cases
        const fallbackResult = await performFallbackCalculation(calculationFormData, formulas);
        if (fallbackResult) {
          console.log("🔄 Using fallback calculation result:", fallbackResult);
          setCalculationResults(fallbackResult);
          toast.success("Calculation completed using fallback method");
        } else {
          setCalculationResults({
            error: {
              result: "Calculation Failed",
              reasoning: formulaData.error
            }
          });
          toast.error("Calculation failed. Please try again or contact support.");
        }
        setIsCalculating(false);
        return;
      }

      // Parse the JSON response from each formula
      const parsedResults = {};
      
      // Handle case where formulaData might be a single string instead of an object
      if (typeof formulaData === 'string') {
        // Clean the string first
        let cleanedString = formulaData.trim();
        console.log("🧹 Processing formulaData as string:", cleanedString);
        
        try {
          const parsed = JSON.parse(cleanedString);
          console.log("✅ Successfully parsed formulaData:", parsed);
          parsedResults['calculation'] = parsed;
        } catch (error) {
          console.log("❌ Failed to parse formulaData as JSON, trying extraction");
          
          // Try to extract JSON from the string
          const jsonMatch = cleanedString.match(/\{.*\}/);
          if (jsonMatch) {
            try {
              const extracted = JSON.parse(jsonMatch[0]);
              console.log("🔧 Extracted JSON from string:", extracted);
              parsedResults['calculation'] = extracted;
            } catch (extractError) {
              console.log("❌ Failed to extract JSON from string");
              parsedResults['calculation'] = {
                result: cleanedString,
                reasoning: "Unable to parse calculation result"
              };
            }
          } else {
            parsedResults['calculation'] = {
              result: cleanedString,
              reasoning: "Calculation completed but result format is unclear"
            };
          }
        }
      } else if (typeof formulaData === 'object' && formulaData !== null) {
        // Process each formula result
        Object.entries(formulaData).forEach(([key, result]) => {
          if (key === 'error' || key === 'calculationErrors') return;
          
          if (typeof result === 'string') {
            try {
              const parsed = JSON.parse(result);
              parsedResults[key] = parsed;
            } catch (parseError) {
              console.log(`❌ Failed to parse result for "${key}" as JSON`);
              parsedResults[key] = {
                result: result,
                reasoning: "Calculation completed but result parsing failed"
              };
            }
          } else if (result && typeof result === 'object') {
            // If it's already an object, use it directly
            console.log(`✅ Using direct object for "${key}":`, result);
            parsedResults[key] = result;
          } else {
            console.log(`❌ No valid result for "${key}"`);
            parsedResults[key] = {
              result: "null",
              reasoning: "No calculation result available"
            };
          }
        });
      }

      console.log("🎉 Final parsed results:", parsedResults);
      
      // Check if we have any valid results
      const validResults = Object.keys(parsedResults).filter(key => 
        parsedResults[key] && 
        parsedResults[key].result && 
        parsedResults[key].reasoning
      );
      
      if (validResults.length === 0) {
        console.warn("⚠️ No valid calculation results found");
        setCalculationResults({
          error: {
            result: "No Results",
            reasoning: "Calculation completed but no valid results were generated. Please check your input data and try again."
          }
        });
        toast.warning("Calculation completed but no results were generated");
      } else {
        setCalculationResults(parsedResults);
        toast.success(`Calculation completed successfully! Generated ${validResults.length} result(s)`);
      }

    } catch (error) {
      console.error("💥 Error in test calculation:", error);
      
      let errorMessage = "Calculation failed. ";
      if (error.message.includes("No calculation formulas")) {
        errorMessage += "This metric doesn't have calculation formulas configured.";
      } else if (error.message.includes("not configured")) {
        errorMessage += "The AI calculation service is not properly configured.";
      } else if (error.message.includes("timed out")) {
        errorMessage += "The calculation request timed out. Please try again.";
      } else if (error.message.includes("connect")) {
        errorMessage += "Unable to connect to the calculation service.";
      } else {
        errorMessage += error.message;
      }
      
      setCalculationResults({
        error: {
          result: "Calculation Error",
          reasoning: errorMessage
        }
      });
      toast.error("Calculation failed. Please try again or contact support.");
    } finally {
      setIsCalculating(false);
      console.log("🏁 Calculation process completed");
    }
  };

  // Fallback calculation function for when AI service fails
  const performFallbackCalculation = async (formData, formulas) => {
    try {
      console.log("🔄 Attempting fallback calculation...");
      
      // Simple fallback for basic calculations
      const fallbackResults = {};
      
      formulas.forEach((formula, index) => {
        if (formula.title && formula.fun) {
          try {
            // Try to extract basic mathematical operations
            const mathExpression = formula.fun.replace(/[^0-9+\-*/().\s]/g, '');
            if (mathExpression.trim()) {
              // For now, just provide a placeholder result
              fallbackResults[formula.title] = {
                result: "0",
                reasoning: `Fallback calculation: Formula ${formula.title} could not be processed. Please check your input data and try again later.`
              };
            }
          } catch (fallbackError) {
            console.log(`⚠️ Fallback calculation failed for ${formula.title}:`, fallbackError);
          }
        }
      });
      
      return Object.keys(fallbackResults).length > 0 ? fallbackResults : null;
    } catch (error) {
      console.log("❌ Fallback calculation failed:", error);
      return null;
    }
  };

  const clickHandler = (metricName, relatedTo) => {
    let metric = metricName.toLowerCase().replaceAll(" ", "-");
    let relatedToName = relatedTo.toLowerCase().replaceAll(" ", "-");

    navigate(`/${currOrg}/metrics/${metric}/${relatedToName}/create`);
    window.location.reload();
  };

  const processMetricData = (data) => {
    console.log("Processing metric data:", data);
    const responseData = data.data[0]?.attributes?.JsonSchema?.properties;
    const fieldOrder = data.data[0]?.attributes?.JsonSchema?.fieldOrder || [];

    console.log("Response data:", responseData);
    console.log("Field order:", fieldOrder);

    if (!responseData) {
      console.warn("No response data found, setting empty schema");
      setFormSchema([]);
      setCollectionInfo(data.data[0]);
      setFormData({});
      setLoader(false);
      return;
    }

    let responseArr = [];
    
    // Use saved field order if available
    if (fieldOrder.length > 0) {
      // Process fields in the saved order
      fieldOrder.forEach(key => {
        if (responseData[key]) {
          responseArr.push([key, responseData[key]]);
        }
      });
      
      // Add any fields not in the order list (backwards compatibility)
      Object.keys(responseData).forEach(key => {
        if (!fieldOrder.includes(key)) {
          responseArr.push([key, responseData[key]]);
        }
      });
    } else {
      // Fallback: use displayOrder or id for backwards compatibility
      responseArr = Object.keys(responseData).map((key) => [
        key,
        responseData[key],
      ]);
      
      responseArr.sort((a, b) => {
        if (a[1].displayOrder !== undefined && b[1].displayOrder !== undefined) {
          return a[1].displayOrder - b[1].displayOrder;
        }
        // Fallback to id-based sorting for old data
        return a[1].id - b[1].id;
      });
    }

    // Check for relation fields and log them
    responseArr.forEach(([fieldKey, fieldConfig], index) => {
      if (fieldConfig.type === "relation") {
        console.log("🔍 RELATION FIELD FOUND:", {
          fieldKey,
          fieldConfig,
          index,
          relatedTo: fieldConfig.relatedTo || fieldConfig.relatedto,
          metricName: fieldConfig.metricname || fieldConfig.metricName
        });
      }
    });

    // Apply framework filtering while preserving field order
    const filteredUrl = `filters[organizations][id][$eqi]=${+orgInfo}`;
    getDataWithFilterUrl("frameworks", filteredUrl)
      .then((frameworkData) => {
        const frameWorkIdList = frameworkData?.data?.map((item) => item?.id) || [];
        console.log("Organization framework IDs:", frameWorkIdList);
        
        // Filter responseArr (which preserves order) based on frameworks
        const filteredResponseArr = responseArr.filter(([key, fieldData]) => {
          const fieldFramework = fieldData?.framework || [];
          console.log(`Field ${key} has frameworks:`, fieldFramework, "comparing against:", frameWorkIdList);
          
          // If field has no framework restrictions (empty array), show it for all frameworks
          if (fieldFramework.length === 0) {
            console.log(`✅ Field ${key} has no framework restrictions - showing for all frameworks`);
            return true;
          }
          
          // If field has specific framework restrictions, check if they match
          const hasMatchingFramework = hasCommonElement(frameWorkIdList, fieldFramework);
          console.log(`🔍 Field ${key} framework check:`, hasMatchingFramework ? "✅ MATCHES" : "❌ NO MATCH");
          return hasMatchingFramework;
        });
        
        console.log("Filtered schema data (preserving order):", filteredResponseArr);
        console.log(`📊 Framework filtering results: ${responseArr.length} total fields -> ${filteredResponseArr.length} visible fields`);
        
        if (filteredResponseArr.length === 0) {
          console.warn("No schema data after framework filtering, using unfiltered data");
          setFormSchema(responseArr);
        } else {
          setFormSchema(filteredResponseArr);
        }
      })
      .catch((error) => {
        console.error("Error in framework filtering:", error);
        // Fallback to unfiltered data on error
        setFormSchema(responseArr);
      });

    setCollectionInfo(data.data[0]);
    const initialFormData = getInitialState(responseArr);
    console.log("Initial form data:", initialFormData);
    setFormData(initialFormData);
    setLoader(false);
  };

  useEffect(() => {
    // Fetch categories first
    fetchCategories();
    
    getDataWithFilterUrl("metrics", filteredUrl)
      .then((data) => {
        if (!data.data || data.data.length === 0) {
          // Try alternative URL encoding approaches
          const alternativeUrl = orgFilterUrl +
            "&filters[Category][$eqi]=" +
            _categoryId.replaceAll("-", " ") +
            "&filters[Name][$eqi]=" +
            _metricId.replaceAll("-", " ").replaceAll("&", "&amp;");
          
          // Try the alternative approach
          getDataWithFilterUrl("metrics", alternativeUrl)
            .then((altData) => {
              if (altData.data && altData.data.length > 0) {
                processMetricData(altData);
              } else {
                // Try to find all metrics in the category to see what names exist
                const broaderUrl = orgFilterUrl + "&filters[Category][$eqi]=" + _categoryId.replaceAll("-", " ");
                getDataWithFilterUrl("metrics", broaderUrl)
                  .then((broaderData) => {
                    // Try to find a partial match
                    const partialMatch = broaderData.data?.find(m => 
                      m.attributes?.Name?.toLowerCase().includes("business conduct") ||
                      m.attributes?.Name?.toLowerCase().includes("financial transparency")
                    );
                    
                    if (partialMatch) {
                      processMetricData({ data: [partialMatch] });
                    } else {
                      setFormSchema([]);
                      setFormData({});
                      setLoader(false);
                    }
                  })
                  .catch((err) => {
                    setFormSchema([]);
                    setFormData({});
                    setLoader(false);
                  });
              }
            })
            .catch((err) => {
              setFormSchema([]);
              setFormData({});
              setLoader(false);
            });
          return;
        }
        
        processMetricData(data);
      })
      .catch((err) => {
        setFormSchema([]);
        setFormData({});
        setLoader(false);
      });
  }, [orgInfo, filteredUrl]);

  // useEffect to handle relation field data fetching
  const processedFieldsRef = useRef(new Set());

  useEffect(() => {
    if (!formSchema || formSchema.length === 0) return;

    console.log("🔄 useEffect: Checking for relation fields that need data fetching");
    
    // Find all relation fields that don't have values yet and haven't been processed
    const relationFieldsToProcess = formSchema
      .map(([fieldKey, fieldConfig], index) => ({ fieldKey, fieldConfig, index }))
      .filter(({ fieldConfig, index }) => {
        const fieldId = fieldConfig.id || `${fieldConfig.title}-${index}`;
        return fieldConfig.type === 'relation' && 
               (!fieldConfig.values || fieldConfig.values.length === 0) &&
               !processedFieldsRef.current.has(fieldId);
      });

    if (relationFieldsToProcess.length === 0) {
      console.log("⏭️ No relation fields need data fetching");
      return;
    }

    console.log(`📊 Found ${relationFieldsToProcess.length} relation fields that need data fetching:`, 
      relationFieldsToProcess.map(f => f.fieldConfig.title));

    // Process each relation field and mark as processed
    relationFieldsToProcess.forEach(({ fieldConfig, index }) => {
      const fieldId = fieldConfig.id || `${fieldConfig.title}-${index}`;
      processedFieldsRef.current.add(fieldId);
      console.log(`🔄 Processing relation field: ${fieldConfig.title} (ID: ${fieldId})`);
      updateFormSchemaWithRealtion(formSchema, index, setFormSchema);
    });
  }, [formSchema]);

  const Tags = collectionInfo?.attributes?.TagList;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Enhanced Page Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div className="flex items-center gap-4">
            {/* Metric Category Badge */}
            <div className="flex items-center gap-2">
              <div className="w-2 h-8 bg-emerald-500 rounded-full"></div>
              <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-neutral-900">
                    {headingTitle}
                  </h1>
                  <Chip 
                    color="success" 
                    variant="flat" 
                    size="sm" 
                    className="text-xs font-semibold bg-emerald-100 text-emerald-800 border-emerald-200"
                  >
                    Active
                  </Chip>
                </div>
                {/* Enhanced Description with Context */}
                <div className="flex items-center gap-3 text-sm text-neutral-600">
                  <span className="font-medium">Category:</span>
                  <Chip 
                    color="default" 
                    variant="bordered" 
                    size="sm" 
                    className="text-xs bg-neutral-50 border-neutral-200 text-neutral-700"
                  >
                    {_categoryId?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Chip>
                  <span className="text-neutral-400">•</span>
                  <span>GHG Emissions • Stationary Combustion</span>
                </div>
                <p className="text-base text-neutral-600 max-w-3xl leading-relaxed mt-2">
                  {description ||
                    `Create and configure your ${headingTitle.toLowerCase()} metric data, calculations, and reporting standards. Set up your metric details for accurate ESG reporting.`}
                </p>
              </div>
            </div>
          </div>
          
          {/* Enhanced Action Buttons */}
          <div className="flex items-center gap-3">
            <Button
              color="danger"
              variant="bordered"
              startContent={<FontAwesomeIcon icon={faTimes} className="text-sm" />}
              onClick={() => navigate(`/${currOrg}/metrics/${_categoryId}`)}
              size="sm"
              className="px-4 py-2 border border-red-300 hover:border-red-400 hover:bg-red-50 transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="enhanced-button text-white px-6 py-2 font-semibold shadow-md hover:shadow-lg"
              startContent={<FontAwesomeIcon icon={faSave} className="text-white text-sm" />}
              onClick={submitHandler}
              size="sm"
            >
              Save & Submit
            </Button>
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-2">
          <LoadingPage isLoading={loader}>
            {/* Enhanced Form Container */}
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
              {/* Enhanced Description Header */}
              <div className="px-6 py-6 border-b border-neutral-200 bg-gradient-to-r from-emerald-50 to-neutral-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-emerald-500 rounded-full"></div>
                  <h2 className="text-xl font-semibold text-neutral-900">Metric Overview</h2>
                  <Chip 
                    color="success" 
                    variant="flat" 
                    size="sm" 
                    className="text-xs bg-emerald-100 text-emerald-800"
                  >
                    New Entry
                  </Chip>
                </div>
                <p className="text-sm text-neutral-600 leading-relaxed max-w-3xl mb-4">
                  {description ||
                    `No description is available at the moment. Please check back later for updates.`}
                </p>
                <div className="mt-4">
                  <MetricFrameworkInfo
                    CollectionInfo={collectionInfo}
                    orgFrameworkDetails={orgFrameworkDetails}
                  />
                </div>
              </div>
              
              {/* Enhanced Form Sections */}
              <div className="p-6">
                
                <div
                  className="p-6 compact-form"
                  style={{
                    opacity: isSubmittingForm ? 0.8 : 1,
                    fontSize: "0.875rem",
                  }}
                >
                    <style>{`
                      /* FORCE FIELD ALIGNMENT - AGGRESSIVE STYLING */
                      .compact-form .field-wrapper {
                        min-height: 120px !important;
                        height: auto !important;
                        display: flex !important;
                        flex-direction: column !important;
                        position: relative !important;
                      }
                      
                      .compact-form .field-container {
                        height: 100% !important;
                        display: flex !important;
                        flex-direction: column !important;
                        justify-content: space-between !important;
                      }
                      
                      /* FORCE LABEL ALIGNMENT */
                      .compact-form .nextui-input-label,
                      .compact-form .nextui-select-label {
                        height: 24px !important;
                        margin-bottom: 8px !important;
                        display: flex !important;
                        align-items: center !important;
                        font-size: 0.875rem !important;
                        font-weight: 500 !important;
                        color: #374151 !important;
                      }
                      
                      /* FORCE HELP TEXT ALIGNMENT */
                      .compact-form .nextui-input-helper-wrapper {
                        height: 20px !important;
                        margin-top: 0 !important;
                        margin-bottom: 8px !important;
                        display: flex !important;
                        align-items: center !important;
                      }
                      
                      /* FORCE INPUT ALIGNMENT AT BOTTOM */
                      .compact-form .nextui-input-wrapper,
                      .compact-form .nextui-select-wrapper,
                      .compact-form .nextui-textarea-wrapper {
                        min-height: 44px !important;
                        height: auto !important;
                        margin-top: 8px !important;
                        margin-bottom: 0 !important;
                        background: #ffffff !important;
                        border: 1px solid #d1d5db !important;
                        border-radius: 6px !important;
                        display: flex !important;
                        align-items: center !important;
                        width: 100% !important;
                        position: relative !important;
                        overflow: visible !important;
                        flex-direction: row !important;
                        justify-content: flex-start !important;
                      }
                      
                      .compact-form .nextui-input,
                      .compact-form .nextui-select-trigger {
                        height: 100% !important;
                        min-height: 44px !important;
                        padding: 8px 12px !important;
                        font-size: 0.875rem !important;
                        background: transparent !important;
                        border: none !important;
                        width: 100% !important;
                        flex: 1 !important;
                        min-width: 0 !important;
                      }
                      
                      /* Force internal input elements to expand fully */
                      .compact-form .nextui-input-wrapper .nextui-input,
                      .compact-form .nextui-input-wrapper input,
                      .compact-form .nextui-input-wrapper [data-slot="input"] {
                        width: 100% !important;
                        min-width: 0 !important;
                        flex: 1 !important;
                        box-sizing: border-box !important;
                        display: block !important;
                        position: relative !important;
                      }
                      
                      /* Override any NextUI internal container constraints */
                      .compact-form .nextui-input-wrapper > div,
                      .compact-form .nextui-input-wrapper > span {
                        width: 100% !important;
                        min-width: 0 !important;
                        flex: 1 !important;
                        display: block !important;
                      }
                      
                      /* Ensure focus state covers the full input area */
                      .compact-form .nextui-input-wrapper:focus-within {
                        border-color: #10b981 !important;
                        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
                        outline: none !important;
                      }
                      
                      /* Override any NextUI internal focus styles */
                      .compact-form .nextui-input-wrapper:focus-within .nextui-input,
                      .compact-form .nextui-input-wrapper:focus-within input,
                      .compact-form .nextui-input-wrapper:focus-within [data-slot="input"] {
                        outline: none !important;
                        border: none !important;
                        box-shadow: none !important;
                      }
                      
                      /* SECTION STYLING */
                      .section-container {
                        background: #ffffff;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 20px;
                        margin-bottom: 20px;
                      }
                      
                      .section-header {
                        background: #f9fafb;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        padding: 16px 20px;
                        margin-bottom: 20px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                      }
                      
                      .section-header-icon {
                        width: 32px;
                        height: 32px;
                        background: #10b981;
                        border-radius: 6px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 16px;
                      }
                      
                      .section-header-content h3 {
                        font-size: 1rem;
                        font-weight: 600;
                        color: #111827;
                        margin: 0;
                      }
                      
                      .section-header-content p {
                        font-size: 0.875rem;
                        color: #6b7280;
                        margin: 2px 0 0 0;
                      }
                      
                      /* BUTTON STYLING */
                      .enhanced-button {
                        background: #10b981 !important;
                        border: none !important;
                        border-radius: 6px !important;
                      }
                      
                      .enhanced-button:hover {
                        background: #059669 !important;
                      }
                      
                      /* ADDITIONAL ALIGNMENT RULES */
                      .compact-form .nextui-input-helper-wrapper {
                        height: 20px !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        display: flex !important;
                        align-items: center !important;
                      }
                      
                      .compact-form .nextui-input-helper-wrapper span {
                        font-size: 0.75rem !important;
                        color: #6b7280 !important;
                        line-height: 1 !important;
                      }
                      
                      /* FORCE GRID ALIGNMENT */
                      .compact-form .grid {
                        align-items: start !important;
                      }
                      
                      /* OVERRIDE ANY NEXTUI DEFAULTS */
                      .compact-form * {
                        box-sizing: border-box !important;
                      }
                    `}</style>

                    {/* Compact Professional Form Content */}
                    <div className="space-y-6">
            {(() => {
              const subheadingStructure = getSubheadingStructure(formSchema);
              
              // Helper function to render individual fields
              const renderField = (fieldInfo, index) => {
                // Safety check - ensure fieldInfo is valid
                if (!fieldInfo || !fieldInfo.config) {
                  console.error("❌ INVALID FIELD INFO:", fieldInfo);
                  return null;
                }
                
                // Safety check - ensure formSchema is ready
                if (!formSchema || formSchema.length === 0) {
                  console.log("⏳ Form schema not ready yet, skipping field:", fieldInfo.key);
                  return null;
                }
                
                const { key: fieldKey, config: fieldConfig } = fieldInfo;
                
                console.log("🔍 RENDERING FIELD:", {
                  fieldKey,
                  fieldType: fieldConfig.type,
                  fieldConfig,
                  index
                });
                
                // Log all field types for debugging
                if (index === 0 && formSchema && formSchema.properties) {
                  console.log("📋 ALL FIELD TYPES IN SCHEMA:", 
                    Object.values(formSchema.properties).map(field => ({
                      name: field.title || field.name,
                      type: field.type
                    }))
                  );
                }
                
                switch (fieldConfig.type) {
                  case "string":
                  case "text":
                    console.log("✅ TEXT FIELD RENDERED SUCCESSFULLY:", fieldKey);
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <TextInput
                          data={fieldConfig}
                          value={formdata?.[fieldKey] || ""}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "password":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <PasswordInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "integer":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <IntegerInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "dropdown":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <DropDownInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          buttonCSS="mb-0"
                        />
                      </div>
                    );
                  case "slider":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <SliderInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                        />
                      </div>
                    );
                  case "boolean":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <BooleanInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "date":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <DateInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "file":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <FileEvidenceInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "relation":
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <RelationDropDownInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          onClickHandler={clickHandler}
                        />
                      </div>
                    );
                  case "metricSelector":
                    console.log("🎯 RENDERING METRIC SELECTOR FIELD:", {
                      fieldKey,
                      fieldConfig,
                      formData: formdata[fieldKey],
                      orgFrameworkDetails,
                      validationErrors
                    });
                    console.log("✅ METRIC SELECTOR FIELD RENDERED SUCCESSFULLY");
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <MetricSelectorInput
                          data={fieldConfig}
                          value={formdata[fieldKey]}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  case "conditional":
                    return (
                      <div key={`${fieldKey}-${index}`} className="md:col-span-2 mb-4">
                        <ConditionalField
                          field={{
                            ...fieldConfig,
                            Title: fieldKey
                          }}
                          formData={formdata}
                          onChange={changeHandler}
                          existingFields={formSchema}
                        />
                      </div>
                    );
                  case "subheading":
                    console.log("🎯 RENDERING SUBHEADING FIELD:", {
                      fieldKey,
                      fieldConfig,
                      title: fieldConfig.title,
                      description: fieldConfig.subheadingDescription
                    });
                    console.log("✅ SUBHEADING FIELD RENDERED SUCCESSFULLY");
                    return (
                      <div key={`${fieldKey}-${index}`} className="space-y-2">
                        <SubheadingField
                          title={fieldConfig.title}
                          description={fieldConfig.subheadingDescription}
                          headingLevel={fieldConfig.headingLevel || "h3"}
                        />
                      </div>
                    );
                  case "textarea":
                    console.log("🎯 RENDERING TEXTAREA FIELD:", {
                      fieldKey,
                      fieldConfig,
                      title: fieldConfig.title
                    });
                    return (
                      <div key={`${fieldKey}-${index}`} className="mb-4">
                        <TextAreaInput
                          data={fieldConfig}
                          value={formdata?.[fieldKey] || ""}
                          orgFrameworkDetails={orgFrameworkDetails}
                          validationErrors={validationErrors}
                          onChangeHandler={changeHandler}
                          cssStyle="mb-0"
                        />
                      </div>
                    );
                  default:
                    console.warn("⚠️ UNKNOWN FIELD TYPE:", {
                      fieldKey,
                      fieldType: fieldConfig.type,
                      fieldConfig
                    });
                    return null;
                }
              };

                            return subheadingStructure.map((section, sectionIndex) => (
                <div key={`section-${sectionIndex}`} className="section-container">
                   {/* Enhanced Section Header */}
                   <div className="section-header">
                     <div className="section-header-icon">
                       <FontAwesomeIcon icon={getSectionIcon(section.subheading.title)} />
                     </div>
                     <div className="section-header-content">
                       <h3 className="field-title text-xl mb-0">
                         {section.subheading.title}
                       </h3>
                       {section.subheading.description && (
                         <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                           {section.subheading.description}
                         </p>
                       )}
                     </div>
                   </div>
                  
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{alignItems: 'start'}}>
                    {section.fields.map((fieldInfo, fieldIndex) => (
                      <div 
                        key={`field-${fieldInfo.key}-${fieldIndex}`}
                        className="field-wrapper"
                        style={{
                          height: '120px', 
                          display: 'flex', 
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        <div className="field-container" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                          {renderField(fieldInfo, fieldIndex)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
                  </div>

                  {/* Compact Professional Action Buttons */}
                  <div className="flex justify-between items-center gap-4 pt-6 border-t border-neutral-200 mt-6">
                    {/* Calculation Status Indicator */}
                    {isFormulaVisible && (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${
                            Object.keys(calculationResults).length > 0 && !calculationResults.error 
                              ? 'bg-emerald-500' 
                              : 'bg-neutral-300'
                          }`}></div>
                          <span className="text-neutral-600">
                            {Object.keys(calculationResults).length > 0 && !calculationResults.error 
                              ? '✓ Ready to calculate' 
                              : 'Fill required fields to calculate'
                            }
                          </span>
                        </div>
                        
                        {/* Field Status Indicators */}
                        {collectionInfo?.attributes?.Name?.toLowerCase().includes("fugitive") && (
                          <div className="flex items-center gap-4 text-xs">
                            <div className={`flex items-center gap-1 ${
                              formdata["Quantity"] && formdata["Quantity"] !== "" 
                                ? 'text-emerald-600' 
                                : 'text-neutral-400'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${
                                formdata["Quantity"] && formdata["Quantity"] !== "" 
                                  ? 'bg-emerald-500' 
                                  : 'bg-neutral-300'
                              }`}></div>
                              Quantity
                            </div>
                            <div className={`flex items-center gap-1 ${
                              formdata["Greenhouse gas"] && formdata["Greenhouse gas"] !== "" 
                                ? 'text-emerald-600' 
                                : 'text-neutral-400'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${
                                formdata["Greenhouse gas"] && formdata["Greenhouse gas"] !== "" 
                                  ? 'bg-emerald-500' 
                                  : 'bg-neutral-300'
                              }`}></div>
                              Greenhouse Gas
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Calculate Button */}
                    {isFormulaVisible && (
                      <div className="flex items-center gap-2">
                        <InfoToolTip 
                          content={
                            <div className="text-sm">
                              <div className="font-medium mb-2">Required for calculation:</div>
                              <div className="space-y-1">
                                <div>• <strong>Quantity:</strong> Enter a positive number</div>
                                <div>• <strong>Greenhouse Gas:</strong> Select gas type</div>
                              </div>
                              <div className="mt-2 text-xs text-neutral-400">
                                Fill in all required fields before calculating
                              </div>
                            </div>
                          }
                          placement="top"
                        />
                        
                        {/* Diagnostic Information */}
                        <div className="text-xs text-neutral-500 bg-neutral-50 px-3 py-2 rounded-md border border-neutral-200">
                          <div className="font-medium mb-1">Service Status:</div>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              process.env.REACT_APP_HAYA_IP_ADDRESS ? 'bg-emerald-500' : 'bg-red-500'
                            }`}></div>
                            <span>
                              {process.env.REACT_APP_HAYA_IP_ADDRESS 
                                ? 'AI Service Configured' 
                                : 'AI Service Not Configured'
                              }
                            </span>
                          </div>
                          {process.env.REACT_APP_HAYA_IP_ADDRESS && (
                            <div className="text-xs text-neutral-400 mt-1">
                              Endpoint: {process.env.REACT_APP_HAYA_IP_ADDRESS}
                            </div>
                          )}
                        </div>

                        <Button
                          color="success"
                          size="sm"
                          onClick={testCalculationHandler}
                          className="enhanced-button text-white px-6 py-2 font-medium text-sm rounded-md"
                          isDisabled={isCalculating}
                          startContent={<FontAwesomeIcon icon={faCalculator} className="text-sm" />}
                        >
                          {isCalculating ? (
                            <Spinner color="white" size="sm" />
                          ) : (
                            "Calculate"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>



            {/* Compact Professional Calculation Results Card */}
            {Object.keys(calculationResults).length > 0 && (
              <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mt-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-8 bg-emerald-500 rounded-full"></div>
                    <div>
                      <h3 className="text-xl font-semibold text-neutral-900">Calculation Results</h3>
                      <p className="text-sm text-neutral-600 mt-1">Emission calculation results</p>
                    </div>
                  </div>

                </div>
                
                <div className="space-y-8">
                  {Object.entries(calculationResults).map(([key, result]) => {
                    if (key === 'error') return null;
                    
                    // Safety check: ensure result has required properties
                    if (!result || typeof result !== 'object') {
                      console.warn(`Invalid result structure for key "${key}":`, result);
                      return null;
                    }
                    
                    // Safety check: ensure result has required properties
                    if (!result.result || !result.reasoning) {
                      console.warn(`Missing required properties for key "${key}":`, result);
                      return null;
                    }
                    
                    // Log the result structure for debugging
                    console.log(`Processing calculation result for "${key}":`, {
                      resultType: typeof result.result,
                      resultValue: result.result,
                      reasoningType: typeof result.reasoning,
                      reasoningValue: result.reasoning
                    });
                    
                    // Convert reasoning to simple text lines for textarea; be careful with mathematical expressions
                    const formatCalculationSteps = (reasoning) => {
                      if (!reasoning) return "";
                      
                      // Handle case where reasoning might be an object
                      let text;
                      if (typeof reasoning === 'object' && reasoning !== null) {
                        text = JSON.stringify(reasoning, null, 2);
                      } else {
                        text = String(reasoning);
                      }
                      
                      text = text.replace(/\r\n/g, '\n');
                      
                      // Only add newlines for actual list markers, not mathematical expressions
                      // Look for numbered bullets: "1. ", "2. " etc. (must have space after dot)
                      text = text.replace(/(^|\n)(\d+)\.\s/g, '$1$2. ');
                      
                      // Look for hyphen bullets: " - " (must have spaces around hyphen)
                      text = text.replace(/(^|\n)\s-\s/g, '$1- ');
                      
                      // Clean up any double newlines
                      text = text.replace(/\n{2,}/g, '\n');
                      
                      return text.trim();
                    };

                    // Extract the correct result from reasoning if the result field seems wrong
                    const extractCorrectResult = (result, reasoning) => {
                      // Handle case where result is an object (e.g., {CO₂e (t): 678.0})
                      if (result && typeof result === 'object' && result !== null) {
                        // Extract the first numeric value from the object
                        const firstValue = Object.values(result).find(val => 
                          typeof val === 'number' || (typeof val === 'string' && !isNaN(parseFloat(val)))
                        );
                        if (firstValue !== undefined) {
                          console.log(`Extracted value from result object: ${firstValue} (original was: ${JSON.stringify(result)})`);
                          return String(firstValue);
                        }
                        // If no numeric value found, return a default
                        return "0";
                      }
                      
                      if (!reasoning) return String(result || "0");
                      
                      const reasoningText = String(reasoning).toLowerCase();
                      
                      // Look for common patterns that indicate the final result
                      const patterns = [
                        // Mathematical calculation pattern: "X t x Y = Z t" (most common)
                        /([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)\s*[x×]\s*[\d,]+\.?\d*\s*=\s*([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)/i,
                        // "total CO₂e is X t" or "final result is X t"
                        /(?:total|final result|result is|co₂e is|co2e is)\s+(?:=?\s*)?([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)/i,
                        // "CO₂e = X t" or "CO2e = X t"
                        /(?:co₂e|co2e)\s*=\s*([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)/i,
                        // "X t CO₂e" or "X tonnes CO2e"
                        /([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)\s+(?:co₂e|co2e)/i,
                        // "Therefore, the final result is CO₂e = X"
                        /(?:therefore|thus|hence|final result|result)\s+(?:is|equals?)\s+(?:co₂e|co2e)\s*=\s*([\d,]+\.?\d*)/i,
                        // Simple calculation result: "= X t" or "= X tonnes"
                        /=\s*([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)/i
                      ];
                      
                      for (const pattern of patterns) {
                        const match = reasoningText.match(pattern);
                        if (match) {
                          // For mathematical calculations, use the result part (after =)
                          const valueToUse = match[2] || match[1];
                          const extractedValue = parseFloat(valueToUse.replace(/,/g, ''));
                          if (!isNaN(extractedValue) && extractedValue > 0) {
                            console.log(`Extracted correct result from reasoning: ${extractedValue} (original was: ${result})`);
                            return extractedValue.toString();
                          }
                        }
                      }
                      
                      // If no pattern matches, try to find the last number followed by "t" or "tonnes"
                      const lastNumberPattern = /([\d,]+\.?\d*)\s*(?:t|tonnes?|tons?)(?:\s|$)/gi;
                      let lastMatch = null;
                      let match;
                      while ((match = lastNumberPattern.exec(reasoningText)) !== null) {
                        lastMatch = match;
                      }
                      
                      if (lastMatch) {
                        const extractedValue = parseFloat(lastMatch[1].replace(/,/g, ''));
                        if (!isNaN(extractedValue) && extractedValue > 0) {
                          console.log(`Extracted last number from reasoning: ${extractedValue} (original was: ${result})`);
                          return extractedValue.toString();
                        }
                      }
                      
                      return String(result || "0");
                    };
                    
                    return (
                      <div key={key} className="border border-neutral-200 rounded-lg p-6 bg-white shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-neutral-800 capitalize flex items-center gap-2">
                            <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </h4>
                          <Chip 
                            color="success" 
                            variant="flat" 
                            size="sm"
                            className="text-xs font-medium bg-emerald-100 text-emerald-800 border-emerald-200"
                          >
                            ✓ Calculated
                          </Chip>
                        </div>
                        
                        <div className="space-y-4">
                          {/* Compact Final Result */}
                          <div className="bg-neutral-50 rounded-md border border-neutral-200 p-4">
                            <label className="block text-sm font-medium text-neutral-700 mb-2">
                              Final Result
                            </label>
                            <div className="flex items-center gap-3">
                              <Input
                                value={extractCorrectResult(result.result, result.reasoning)}
                                isReadOnly
                                className="bg-white border-neutral-200 text-lg font-semibold text-emerald-700"
                                size="md"
                              />
                              <span className="text-sm text-neutral-600">t CO₂e</span>
                            </div>
                            {/* Debug info - remove in production */}
                            {process.env.NODE_ENV === 'development' && (
                              <div className="text-xs text-gray-500 mt-2">
                                Debug: Original result: {typeof result.result === 'object' ? JSON.stringify(result.result) : String(result.result || 'null')} | Extracted: {extractCorrectResult(result.result, result.reasoning)}
                              </div>
                            )}
                          </div>
                          
                          {/* Compact Calculation Steps and Details */}
                          <div className="bg-neutral-50 rounded-md border border-neutral-200 p-4">
                            <label className="block text-sm font-medium text-neutral-700 mb-3">
                              Calculation Steps & Details
                            </label>
                            <Textarea
                              isReadOnly
                              value={formatCalculationSteps(result.reasoning)}
                              minRows={6}
                              size="sm"
                              className="bg-white border-neutral-200 w-full"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Compact Professional Action Buttons */}
                <div className="flex justify-center items-center mt-6 pt-6 border-t border-neutral-200">
                  <div className="flex gap-4">
                    <Button
                      color="default"
                      variant="bordered"
                      size="sm"
                      onClick={testCalculationHandler}
                      className="border-neutral-300 hover:border-neutral-400 text-neutral-700 hover:text-neutral-800 px-6 font-medium"
                      isDisabled={isCalculating}
                      startContent={<FontAwesomeIcon icon={faCalculator} className="text-sm" />}
                    >
                      {isCalculating ? (
                        <Spinner color="default" size="sm" />
                      ) : (
                        "Recalculate"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </LoadingPage>
        </div>
      </BreadCrumbsPage>
    </div>
  );
};

export default CreateMetricForm;